import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import {
  BILLABLE_HOURS_PER_DAY,
  type OfferPhase,
} from "@/lib/offer-calc";
import {
  computeOfferWritingHours,
  includeDefaultLine,
} from "@/lib/offer-mutation-helpers";
import type { OfferLine, OfferPricingType } from "./use-offers";

/**
 * Henter mal-linjer for gitte template-koder og inserter dem som offer_lines
 * på et tilbud. Brukes både ved opprettelse (VIB-553) og fra
 * tilbudsinnstillinger på et eksisterende tilbud.
 *
 * VIB-701: sats hentes fra koblet `offer_line_templates.default_rate` (via
 * `line_template_id`-FK). Fallback til 0 hvis NULL.
 */
export async function insertTemplateLinesForOffer(
  offerId: string,
  templateCodes: string[],
  sortOrderOffset = 0,
): Promise<number> {
  if (templateCodes.length === 0) return 0;
  const { data: templateLines, error: linesErr } = await supabase
    .from("offer_template_lines")
    .select(
      `
        quantity, sort_order, line_template_id,
        template:offer_templates!inner(code),
        line_template:offer_line_templates(*)
      `,
    )
    .in("template.code", templateCodes)
    .order("sort_order");
  if (linesErr) throw linesErr;
  if (!templateLines || templateLines.length === 0) return 0;

  type OfferLineTemplate = {
    id: string;
    phase: OfferPhase;
    name: string;
    cost_type: string;
    pricing_type: OfferPricingType;
    default_unit: string | null;
    default_rate: number | string | null;
    default_quantity: number | string | null;
    sort_order: number | null;
  };
  type TemplateLineRow = {
    quantity: number | string | null;
    sort_order: number | null;
    line_template_id: string | null;
    template: { code: string } | null;
    line_template: OfferLineTemplate | null;
  };

  const insertRows = (templateLines as unknown as TemplateLineRow[])
    .map((tl, idx) => {
      // VIB-701: katalogen er autoritativ. Hopp over mal-linjer som
      // ikke er matchet (line_template_id NULL) — admin må rette dem
      // før de tas i bruk.
      const lt = tl.line_template;
      if (!lt) {
        console.warn(
          `[insertTemplateLinesForOffer] Mal-linje uten line_template hoppet over (template_code: ${tl.template?.code ?? "?"})`,
        );
        return null;
      }
      const isInternalHourly =
        lt.cost_type === "internal" && lt.pricing_type === "hourly";
      const unit = lt.default_unit || "t";
      const unit_price = lt.default_rate != null ? Number(lt.default_rate) : 0;
      const qty = Number(tl.quantity) || 0;
      const effectiveHours = isInternalHourly
        ? unit === "d"
          ? qty * BILLABLE_HOURS_PER_DAY
          : qty
        : 0;
      const amount = isInternalHourly
        ? Math.round(effectiveHours * unit_price)
        : Math.round(qty * unit_price);
      return {
        offer_id: offerId,
        template_id: lt.id, // peker til katalog-raden
        phase: lt.phase,
        description: lt.name,
        cost_type: lt.cost_type,
        pricing_type: lt.pricing_type,
        quantity: qty,
        unit,
        unit_price,
        amount,
        sort_order: (tl.sort_order ?? idx * 10) + sortOrderOffset,
      };
    })
    .filter((row): row is NonNullable<typeof row> => row !== null);

  if (insertRows.length === 0) return 0;
  const { error: insertErr } = await supabase.from("offer_lines").insert(insertRows);
  if (insertErr) throw insertErr;
  return insertRows.length;
}

/**
 * Inserter alle offer_line_templates som er flagget med
 * is_default_for_offers (f.eks. Reisetid 4t). Kalles ved opprettelse av
 * nye tilbud så folk ikke glemmer å legge til reisetid. Kan fjernes
 * manuelt fra editoren hvis ikke aktuelt.
 *
 * VIB-1054: når reise faktureres separat (medgått) skal den faste reise-
 * defaultlinja IKKE legges til — reisetid håndteres som medgått på prosjektet,
 * og en reise-linje i tilbudet gir dobbel Reisetid ved konvertering.
 */
export async function insertDefaultLinesForOffer(
  offerId: string,
  hourlyRate: number,
  billsTravelPerMedgatt = false,
): Promise<number> {
  const { data: defaults, error } = await supabase
    .from("offer_line_templates")
    .select("*")
    .eq("is_active", true)
    .eq("is_default_for_offers", true)
    .order("phase")
    .order("sort_order");
  if (error) throw error;
  if (!defaults || defaults.length === 0) return 0;

  type DefaultTemplate = {
    id: string;
    phase: OfferPhase;
    name: string;
    cost_type: string;
    pricing_type: OfferPricingType;
    default_unit: string | null;
    default_rate: number | string | null;
    default_quantity: number | string | null;
    sort_order: number | null;
  };
  // VIB-1054: dropp reise-defaultlinja når reise er medgått.
  const templates = (defaults as unknown as DefaultTemplate[]).filter((t) =>
    includeDefaultLine(t, billsTravelPerMedgatt),
  );
  if (templates.length === 0) return 0;
  const insertRows = templates.map((t, idx) => {
    const isInternalHourly =
      t.cost_type === "internal" && t.pricing_type === "hourly";
    const unit = isInternalHourly ? t.default_unit || "t" : "stk";
    // VIB-701: fallback 0.
    const unit_price = t.default_rate != null ? Number(t.default_rate) : 0;
    const qty = Number(t.default_quantity) || 0;
    const effectiveHours = isInternalHourly
      ? unit === "d"
        ? qty * BILLABLE_HOURS_PER_DAY
        : qty
      : 0;
    const amount = isInternalHourly
      ? Math.round(effectiveHours * unit_price)
      : Math.round(qty * unit_price);
    return {
      offer_id: offerId,
      template_id: t.id,
      phase: t.phase,
      description: t.name,
      cost_type: t.cost_type,
      pricing_type: t.pricing_type,
      quantity: qty,
      unit,
      unit_price,
      amount,
      sort_order: (t.sort_order ?? idx * 10) - 1000,
    };
  });
  const { error: insertErr } = await supabase
    .from("offer_lines")
    .insert(insertRows);
  if (insertErr) throw insertErr;
  return insertRows.length;
}

/**
 * Template-id for "Konseptutvikling" (preprod / rolle / internal / hourly).
 * VIB-663a: Auto-tilbudsskriving-linja rutes hit når offer.concept_routing
 * er true, i stedet for default Tilbudsskriving under Prosjektledelse.
 */
const CONCEPT_DEVELOPMENT_TEMPLATE_ID = "5a572fee-8996-4464-adb5-967841c81e8c";

/**
 * Template-id for "Prosjektledelse" (pl / rolle / internal / hourly).
 * Default Tilbudsskriving-credit kobles hit slik at sats hentes fra
 * katalogen istedenfor global hourly_rate.
 */
const PROJECT_LEAD_TEMPLATE_ID = "5f083f8c-1a3d-4e22-8b61-9b6fdb3fb64c";

/**
 * Setter inn eller oppdaterer den auto-genererte tilbudsskriving-linjen
 * (auto_kind='offer_writing') på et tilbud. Default plasseres den under
 * Prosjektledelse som "Tilbudsskriving"; hvis `conceptRouting=true` rutes
 * den til Konseptutvikling-malen i preprod (VIB-663a). Ved accepted-status
 * oppdateres ikke linjen — den fryses så tilbudsskriver får credit basert
 * på BF ved aksept-tidspunktet.
 *
 * VIB-701-followup: sats hentes fra katalog-raden (offer_line_templates.
 * default_rate) for konsistens med resten av tilbud-linjene. `hourlyRate`
 * brukes kun som fallback hvis katalog-raden mangler eller har NULL rate.
 */
export async function upsertOfferWritingLine(params: {
  offerId: string;
  bfAmount: number;
  hourlyRate: number;
  isFrozen: boolean;
  conceptRouting?: boolean;
}): Promise<void> {
  const { offerId, bfAmount, hourlyRate, isFrozen, conceptRouting = false } = params;
  const templateId = conceptRouting ? CONCEPT_DEVELOPMENT_TEMPLATE_ID : PROJECT_LEAD_TEMPLATE_ID;

  // Hent eksisterende auto-linje (om noen) for dette tilbudet
  const { data: existing, error: existingErr } = await supabase
    .from("offer_lines")
    .select("id, quantity")
    .eq("offer_id", offerId)
    .eq("is_auto_generated", true)
    .eq("auto_kind", "offer_writing")
    .maybeSingle();
  if (existingErr) throw existingErr;

  // Hent multiplikator fra system_config (default 2)
  const { data: cfgRow } = await supabase
    .from("system_config")
    .select("value")
    .eq("key", "offer_writing_hours_per_100k_bf")
    .maybeSingle();
  const hoursPer100k = Number(cfgRow?.value) || 2;
  const qty = computeOfferWritingHours(bfAmount, hoursPer100k);

  // Hent sats fra katalog (VIB-701-followup). Fall til hourlyRate hvis
  // raden mangler default_rate (skal ikke skje etter backfill, men er
  // forsvarlig fallback).
  const { data: templateRow } = await supabase
    .from("offer_line_templates")
    .select("default_rate")
    .eq("id", templateId)
    .maybeSingle();
  const unitPrice =
    templateRow?.default_rate != null ? Number(templateRow.default_rate) : hourlyRate;
  const amount = Math.round(qty * unitPrice);

  // Skip oppdatering hvis frosset (status accepted/sent og verdien
  // allerede er satt). Erik vil at krediten skal være statisk når
  // tilbudet sendes/aksepteres.
  if (isFrozen && existing) return;

  const routingFields = conceptRouting
    ? {
        phase: "preprod" as const,
        description: "Konseptutvikling",
        template_id: CONCEPT_DEVELOPMENT_TEMPLATE_ID,
      }
    : {
        phase: "pl" as const,
        description: "Tilbudsskriving",
        template_id: PROJECT_LEAD_TEMPLATE_ID,
      };

  if (existing) {
    const { error } = await supabase
      .from("offer_lines")
      .update({
        quantity: qty,
        unit_price: unitPrice,
        amount,
        ...routingFields,
      })
      .eq("id", existing.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from("offer_lines").insert({
      offer_id: offerId,
      cost_type: "internal",
      pricing_type: "hourly",
      quantity: qty,
      unit: "t",
      unit_price: unitPrice,
      amount,
      is_auto_generated: true,
      auto_kind: "offer_writing",
      sort_order: -2000,
      ...routingFields,
    });
    if (error) throw error;
  }
}

// ── Offer lines mutations ────────────────────────────────────

interface CreateOfferLineInput {
  offer_id: string;
  template_id?: string | null;
  phase: OfferPhase;
  description: string;
  cost_type: string;
  pricing_type: OfferPricingType;
  quantity?: number;
  unit: string;
  unit_price?: number;
  purchase_price?: number;
  amount?: number;
  comment?: string | null;
  sort_order?: number;
  /** VIB-663e: tildel linja en tilvalgsseksjon. null/undefined = hovedbudsjett. */
  section_id?: string | null;
  /** Settes ved angre-gjenoppretting så slettede linjer kommer tilbake intakt. */
  overtime_split?: OfferLine["overtime_split"];
  auto_kind?: string | null;
  is_auto_generated?: boolean;
  /** VIB-2097: tidsbasis (kr/t). Settes ved katalogvalg for t/d-linjer. */
  rate_basis_hourly?: number | null;
  /** VIB-2980: Idé-kortet linjen lages fra («Fra idé»). */
  source_idea_card_id?: string | null;
}

export function useCreateOfferLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateOfferLineInput): Promise<OfferLine> => {
      const { data, error } = await supabase
        .from("offer_lines")
        .insert({
          offer_id: input.offer_id,
          template_id: input.template_id ?? null,
          phase: input.phase,
          description: input.description,
          cost_type: input.cost_type,
          pricing_type: input.pricing_type,
          quantity: input.quantity ?? 0,
          unit: input.unit,
          unit_price: input.unit_price ?? 0,
          purchase_price: input.purchase_price ?? 0,
          amount: input.amount ?? 0,
          comment: input.comment ?? null,
          sort_order: input.sort_order ?? 0,
          section_id: input.section_id ?? null,
          overtime_split: input.overtime_split ?? null,
          auto_kind: input.auto_kind ?? null,
          is_auto_generated: input.is_auto_generated ?? false,
          rate_basis_hourly: input.rate_basis_hourly ?? null,
          source_idea_card_id: input.source_idea_card_id ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      return data as OfferLine;
    },
    onSuccess: async (_, variables) => {
      await invalidateFor(queryClient, "offer_lines", { offerId: variables.offer_id });
    },
  });
}

export function useUpdateOfferLine(offerId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      id: string;
      patch: Partial<Omit<OfferLine, "id" | "offer_id">>;
    }): Promise<void> => {
      const { error } = await supabase
        .from("offer_lines")
        .update(params.patch)
        .eq("id", params.id);
      if (error) throw error;
    },
    onSuccess: async () => {
      if (offerId) {
        await invalidateFor(queryClient, "offer_lines", { offerId });
      }
    },
  });
}

export function useDeleteOfferLine(offerId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from("offer_lines").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      if (offerId) {
        await invalidateFor(queryClient, "offer_lines", { offerId });
      }
    },
  });
}

/**
 * Legg til linjer fra valgte tilbudsmaler på et eksisterende tilbud.
 * Sort_order forskyves slik at nye linjer havner etter eksisterende.
 */
export function useAddOfferTemplateLines() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      offerId: string;
      templateCodes: string[];
    }): Promise<number> => {
      const { data: existing, error: existingErr } = await supabase
        .from("offer_lines")
        .select("sort_order")
        .eq("offer_id", params.offerId);
      if (existingErr) throw existingErr;
      const offset =
        (existing?.length ?? 0) > 0
          ? Math.max(...existing!.map((r) => r.sort_order)) + 100
          : 0;

      // VIB-701: sats hentes fra katalogen via line_template-relasjonen
      // i insertTemplateLinesForOffer. Ingen hourlyRate trengs lenger.
      return insertTemplateLinesForOffer(params.offerId, params.templateCodes, offset);
    },
    onSuccess: async (_, variables) => {
      await invalidateFor(queryClient, "offer_lines", { offerId: variables.offerId });
    },
  });
}
